/**
 * PartnerReviewHost — scheduler(상세창 시공파트너 카드)의 "이력·평가보기 ›"를 받아
 * PartnerReviewDrawer(시공이력·평가) 를 여는 호스트.
 *
 * scheduler 패키지는 shell-web 을 import 하지 않으므로(D-03 §4.1.5) SchedulerHostProvider 로 콜백을 주입한다.
 * AppFrame 의 Outlet 바깥에 한 번만 두어 /agents/scheduler 라우트와 SiteWorkspaceDrawer 두 경로가 같은 콜백을 받는다.
 * Drawer 는 children(SiteWorkspaceDrawer 포함)의 자식이 아니라 형제로 뒤에 렌더한다.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { SchedulerHostProvider } from '@indipia/agents-scheduler';
import { PartnerReviewDrawer } from './PartnerReviewDrawer';

interface Selected { kind: 'partner' | 'vendor'; id: number; name: string }

export function PartnerReviewHost({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<Selected | null>(null);

  const navigate = useNavigate();

  const openPartnerHistory = useCallback(
    (p: { partnerId: number; partnerName: string }) => setSelected({ kind: 'partner', id: p.partnerId, name: p.partnerName }),
    [],
  );
  // 자재파트너(거래처) 거래이력 — 같은 드로어를 kind="vendor" 로 연다
  const openVendorHistory = useCallback(
    (p: { vendorId: number; vendorName: string }) => setSelected({ kind: 'vendor', id: p.vendorId, name: p.vendorName }),
    [],
  );
  // 나의 할일(/todo)로 이동
  const openTodo = useCallback(() => navigate('/todo'), [navigate]);
  // 신호·할일 처리 버튼 — 서버가 내려준 화면 주소(webPath)로 이동(작업보고 확인 · 작업요청 작성 등)
  const openPath = useCallback((path: string) => navigate(path), [navigate]);
  // 작업요청 작성(인디공사현황) — 간트 [작업요청 작성]. 현장(siteId)을 주면 작업요청 작성 화면이 그 현장을 기본 선택한다
  const openWorkOrder = useCallback(
    (p?: { siteId?: number | null }) =>
      navigate(p?.siteId ? `/agents/work-log/workorder/new?siteId=${p.siteId}` : '/agents/work-log/workorder/new'),
    [navigate],
  );
  const host = useMemo(() => ({ openPartnerHistory, openVendorHistory, openTodo, openPath, openWorkOrder }), [openPartnerHistory, openVendorHistory, openTodo, openPath, openWorkOrder]);

  return (
    <SchedulerHostProvider value={host}>
      {children}
      {selected && (
        <PartnerReviewDrawer
          key={`${selected.kind}-${selected.id}`}
          kind={selected.kind}
          id={selected.id}
          name={selected.name}
          onClose={() => setSelected(null)}
        />
      )}
    </SchedulerHostProvider>
  );
}
